import { useEffect, useRef, useState } from 'react';
import { apiUrl } from '@territory/client/routes';
import { bootstrapWedgeAuth } from '@territory/client/api';
import { Splash } from './Splash';
import { WedgeLanding } from './WedgeLanding';
import { clearWedgeLocalState } from './clearWedgeState';

/**
 * 2026-05-29 · Deep-link auto sign-in.
 *
 * When an emailed "Open PREP with my code" link carries BOTH ?code= and
 * ?email=, sign the rep in automatically instead of dropping them on the
 * access-code form to re-type what they were already sent. Success hard-
 * navigates to / so they land straight in PREP on a fresh start. Failure
 * (expired / wrong code) falls back to WedgeLanding with the reason shown, so
 * they can request a new link.
 *
 * SHARP-* referral codes route to the referral endpoint, FINCO-* promo codes
 * to the promo endpoint, everything else to the access-code endpoint. No new
 * attack surface · this is the same redeem the user would do by
 * hand; it just skips the typing for a link they clicked themselves.
 */
export function CodeAutoRedeem({ code, email }: { code: string; email: string }) {
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      const e = email.trim();
      const c = code.trim();
      const isReferral = c.toUpperCase().startsWith('SHARP-');
      /* 2026-07-22 · self-serve promo codes (FINCO-*) route to the promo
         redemption endpoint · same auto-redeem path as SHARP/PREP codes. */
      const isPromo = c.toUpperCase().startsWith('FINCO-');
      const firstEndpoint = isPromo
        ? '/api/wedge/referrals/promo'
        : isReferral
          ? '/api/wedge/referrals/redeem'
          : '/api/auth/wedge-access/redeem-code';
      try {
        const res = await fetch(apiUrl(firstEndpoint), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email: e, code: c }),
        });
        if (!res.ok) {
          const j = (await res.json().catch(() => ({}))) as { message?: string };
          throw new Error(j?.message || `Could not sign you in (${res.status}).`);
        }
        /* Referral + promo codes provision the account but never sign the
           browser in (the email is unverified): request the sign-in link for
           mailbox proof and hand the rep to the code screen with the note. */
        const okBody = (await res.clone().json().catch(() => ({}))) as { signInRequired?: boolean };
        if (okBody && okBody.signInRequired) {
          await fetch(apiUrl('/api/auth/wedge-access/sign-in-link'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email: e }),
          }).catch(() => undefined);
          setError(`Almost in. We emailed a sign-in link to ${e}. Open it on this device to finish.`);
          return;
        }
        /* fresh start · drop the prior session's cached profile/XP/streak so a
           reset account re-onboards instead of rehydrating the old session.
           2026-07-10 · MUST run BEFORE bootstrapWedgeAuth (matching
           AuthCallbackWedge): the old order (a) let bootstrap present a
           STALE rv_wedge_rt from a prior device session instead of the
           fresh redeem cookie, and (b) wiped the body tokens bootstrap had
           just captured, leaving code sessions cookie-only · which is what
           killed the idle Monster-demo session at access-token expiry. */
        clearWedgeLocalState();
        /* 07-27 remediation audit · redeem = possible identity swap; the
           offline session cache is rv_auth_-prefixed so clearWedgeLocalState
           misses it. Drop it before the new session hydrates. */
        try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
        await bootstrapWedgeAuth().catch(() => {
          /* non-fatal · the session cookie is already set */
        });
        /* 2026-07-10 · success hard-navigates to / per the contract above.
           The old <WedgeLanding initialView="pinsetup"> handoff dead-ended a
           just-signed-in rep on the sign-in screen: WedgeLanding ignores
           initialView since the 2026-06-02 login simplification, and the auth
           gate does not re-evaluate until a full reload. replace() also strips
           the spent ?code so the fresh load enters PREP directly. */
        window.location.replace('/');
      } catch (ex) {
        setError(ex instanceof Error ? ex.message : 'Could not sign you in automatically. Enter your code below.');
      }
    })();
  }, [code, email]);

  if (error) {
    return (
      <WedgeLanding
        initialView="code"
        initialCode={code}
        initialEmail={email}
        initialError={error}
      />
    );
  }
  return <Splash label="Signing you in" />;
}
